import type { ErrorCode } from '@hadir/shared';

import { config } from '@/config';

/**
 * Typed fetch client for the Hadir REST API (05 §5.2 `api/client.ts`).
 *
 * Responsibilities, and nothing else:
 *  - build the URL from `config.apiUrl` (`/api/v1` in dev — Caddy keeps it same-origin)
 *  - send JSON, send cookies (`credentials: 'include'` for the refresh cookie)
 *  - attach `Authorization: Bearer <access token>` from the auth store
 *  - parse the `{ code, message, details, requestId }` envelope into an `ApiError`
 *  - on 401: single-flight refresh, retry the original request exactly once,
 *    hard-logout when the refresh itself fails
 *
 * It deliberately does not import the auth store: the store owns the session and
 * calls `setSessionBridge()` once at module load, which keeps the dependency
 * one-way (store → client) and the client trivially testable.
 */

/** The API error envelope (04 §9.3). `code` is an `ErrorCode` for known failures. */
export interface ApiErrorEnvelope {
  code: ErrorCode | string;
  message: string;
  details?: unknown;
  requestId?: string;
}

export class ApiError extends Error {
  readonly code: ErrorCode | string;
  readonly status: number;
  readonly details?: unknown;
  readonly requestId?: string;
  /** Seconds from the `Retry-After` header; set for `RATE_LIMITED` (05 §6.3). */
  readonly retryAfter?: number;

  constructor(init: {
    code: ErrorCode | string;
    message: string;
    status: number;
    details?: unknown;
    requestId?: string;
    retryAfter?: number;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.requestId = init.requestId;
    this.retryAfter = init.retryAfter;
  }

  /** True when the failure is the browser/network rather than the API. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Serialised as JSON unless it is already a `FormData`/`Blob`/string. */
  body?: unknown;
  /** Appended as a query string; `null`/`undefined` entries are dropped. */
  query?: Record<string, QueryValue | QueryValue[]>;
  /** Set false for the public endpoints (login, refresh). Default true. */
  auth?: boolean;
  /** Internal: prevents the 401 → refresh → retry loop from recursing. */
  _retried?: boolean;
}

/* ── The session bridge ───────────────────────────────────────────────────── */

/**
 * The four things the client needs from the session, injected by the auth store
 * so the module graph stays acyclic.
 */
export interface SessionBridge {
  getAccessToken: () => string | null;
  /** Called with the payload of a successful `POST /auth/staff/refresh`. */
  onRefreshed: (session: { accessToken: string; expiresIn: number }) => void;
  /** Called once when a 401 could not be recovered: drop the session. */
  onUnauthenticated: () => void;
}

let bridge: SessionBridge = {
  getAccessToken: () => null,
  onRefreshed: () => undefined,
  onUnauthenticated: () => undefined,
};

export function setSessionBridge(next: SessionBridge): void {
  bridge = next;
}

/* ── URL helpers ──────────────────────────────────────────────────────────── */

export function joinUrl(base: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

function buildQuery(query: RequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (entry === undefined || entry === null) continue;
        params.append(key, String(entry));
      }
    } else {
      params.append(key, String(value));
    }
  }
  const serialised = params.toString();
  return serialised ? `?${serialised}` : '';
}

/* ── Response parsing ─────────────────────────────────────────────────────── */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // Not JSON (an HTML error page from a proxy, say) — keep the raw text.
    return text;
  }
}

/**
 * Normalise whatever the failing response carried into an envelope. Accepts the
 * envelope at the top level and nested under `error`, so a proxy-shaped body or a
 * plain Nest exception still produces a usable code.
 */
function toEnvelope(payload: unknown, status: number): ApiErrorEnvelope {
  const source = isRecord(payload) && isRecord(payload.error) ? payload.error : payload;

  if (isRecord(source)) {
    const code = typeof source.code === 'string' ? source.code : fallbackCode(status);
    const message = typeof source.message === 'string' ? source.message : code;
    const envelope: ApiErrorEnvelope = { code, message };
    if (source.details !== undefined) envelope.details = source.details;
    if (typeof source.requestId === 'string') envelope.requestId = source.requestId;
    return envelope;
  }

  const code = fallbackCode(status);
  return { code, message: typeof payload === 'string' && payload ? payload : code };
}

/** Best-effort code when the body carries none — keeps `errors.<CODE>` lookups working. */
function fallbackCode(status: number): ErrorCode | string {
  switch (status) {
    case 401:
      return 'AUTH_REQUIRED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 422:
      return 'VALIDATION_ERROR';
    case 429:
      return 'RATE_LIMITED';
    default:
      return 'INTERNAL';
  }
}

function parseRetryAfter(response: Response): number | undefined {
  const header = response.headers.get('Retry-After');
  if (!header) return undefined;
  const seconds = Number(header);
  return Number.isFinite(seconds) ? seconds : undefined;
}

/* ── Refresh: single-flight, retry once ───────────────────────────────────── */

/** 401 codes that a token refresh can plausibly fix (04 §9.3). */
const REFRESHABLE_CODES = new Set<string>([
  'AUTH_REQUIRED',
  'AUTH_TOKEN_EXPIRED',
  'AUTH_TOKEN_INVALID',
]);

/**
 * The single-flight guard: ten concurrent 401s share ONE `POST /auth/staff/refresh`.
 * Cleared in `finally`, so the next 401 after a completed refresh starts a new one.
 */
let refreshInFlight: Promise<boolean> | null = null;

/** Shape of `POST /auth/staff/refresh` (04 §4.1 → `AccessTokenResult`). */
interface AccessTokenResponse {
  accessToken: string;
  expiresIn: number;
}

/**
 * Exchange the httpOnly `hadir_rt` cookie for a new access token.
 *
 * Uses bare `fetch` rather than `request()` on purpose: a refresh must never be
 * able to trigger the 401 → refresh path recursively.
 */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(joinUrl(config.apiUrl, '/auth/staff/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
        /*
         * `keepalive` because a refresh is not an ordinary request: it ROTATES
         * the cookie, and the server commits that rotation whether or not this
         * document is still around to hear the answer.
         *
         * Without it, a navigation during the boot refresh (a TA clicking a
         * bookmark while the dashboard is still starting, or a double reload)
         * tears the fetch down after the server has already rotated. The new
         * token is then lost, the next boot presents the old one, and
         * `rotateStaff` correctly reads that as token theft — it revokes the
         * whole chain and the TA is signed out with no explanation.
         *
         * Keeping the request alive past teardown lets the `Set-Cookie` land,
         * so the rotation the server performed is the rotation the browser has.
         */
        keepalive: true,
      });
      if (!response.ok) return false;

      const payload = (await readJson(response)) as Partial<AccessTokenResponse> | undefined;
      if (!payload || typeof payload.accessToken !== 'string') return false;

      bridge.onRefreshed({
        accessToken: payload.accessToken,
        expiresIn: typeof payload.expiresIn === 'number' ? payload.expiresIn : 900,
      });
      return true;
    } catch {
      // Network failure: not an auth decision, so do not clear the session.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** Test seam: forget any in-flight refresh between tests. */
export function resetRefreshState(): void {
  refreshInFlight = null;
}

/* ── The request function ─────────────────────────────────────────────────── */

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, auth = true, _retried = false, ...rest } = options;

  const headers = new Headers(rest.headers);
  headers.set('Accept', 'application/json');

  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
      payload = body;
    } else {
      payload = JSON.stringify(body);
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    }
  }

  if (auth) {
    const token = bridge.getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const url = `${joinUrl(config.apiUrl, path)}${buildQuery(query)}`;

  let response: Response;
  try {
    response = await fetch(url, {
      ...rest,
      method,
      headers,
      // The refresh cookie is httpOnly and scoped to /api/v1/auth; sending
      // credentials on every call also keeps the prod cross-subdomain setup working.
      credentials: 'include',
      ...(payload !== undefined ? { body: payload } : {}),
    });
  } catch (cause) {
    throw new ApiError({
      code: 'NETWORK_ERROR',
      message: cause instanceof Error ? cause.message : 'Network request failed',
      status: 0,
    });
  }

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await readJson(response)) as T;
  }

  const envelope = toEnvelope(await readJson(response), response.status);

  // 401 → refresh once → replay the original request (05 §5.2, §6.3).
  if (response.status === 401 && auth && REFRESHABLE_CODES.has(String(envelope.code))) {
    if (!_retried) {
      const refreshed = await refreshSession();
      if (refreshed) return request<T>(path, { ...options, _retried: true });
    }
    // Either the refresh failed (cookie gone, or reuse revoked the chain) or the
    // replay was rejected too. Both mean this session is over: hard logout, and
    // the guards redirect to `/login?next=…` off the `anonymous` status.
    bridge.onUnauthenticated();
  }

  const retryAfter = parseRetryAfter(response);
  throw new ApiError({
    code: envelope.code,
    message: envelope.message,
    status: response.status,
    ...(envelope.details !== undefined ? { details: envelope.details } : {}),
    ...(envelope.requestId !== undefined ? { requestId: envelope.requestId } : {}),
    ...(retryAfter !== undefined ? { retryAfter } : {}),
  });
}

/** Verb helpers. Feature modules write their own resource calls on top of these. */
export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
} as const;

/*
 * The cursor-paginated envelope `{ items, nextCursor }` is NOT declared here:
 * `Paginated<T>` already ships from `@hadir/shared` (04 §4), and a second copy
 * would be a second truth.
 */
